// Commercial Acoustic Calculator — engine solution builders (3 tiers).
// Depends on window.AcousticCalculatorEngine from engine-core.js.

(function () {
  "use strict";

  const ns = window.AcousticCalculatorEngine;
  if (!ns) {
    throw new Error(
      "AcousticCalculatorEngine core not loaded. Include acoustic-calculator-commercial-engine-core.js before engine-solutions.js.",
    );
  }
  const {
    BANDS,
    RT60_CONSTANT,
    round4,
    calculateRT60,
    calculateRequiredAbsorption,
    calculateExistingAbsorption,
    calculateRT60WithProducts,
  } = ns;
  const MIN_SINGLE_PRODUCT_RT60 = 0.05;

  function calculateProductQuantity(requiredSabins, productAbsorption) {
    if (productAbsorption === 0) return 0;
    return Math.ceil(requiredSabins / productAbsorption);
  }

  // Match CSV formula: ROUNDUP(MAX(((0.049*Volume)/(Target*Abs)) - (Existing/Abs), 0), 0)
  function findQuantityToCloseBand(frequency, productType, targetRT60, ctx) {
    const product =
      productType === "wall" ? ctx.wallProduct : ctx.ceilingProduct;
    if (!product) return 0;
    const productAbs = product.absorption[frequency];
    if (!productAbs) return 0;

    const existing = calculateExistingAbsorption(frequency, ctx);
    const requiredUnits =
      (RT60_CONSTANT * ctx.roomVolume) / (targetRT60 * productAbs);
    const existingUnits = existing / productAbs;
    return Math.ceil(Math.max(requiredUnits - existingUnits, 0));
  }

  function countClosedBands(rt60Values, targetRT60) {
    let count = 0;
    const bandsClosed = [];
    BANDS.forEach((band, index) => {
      const rt60 = rt60Values[band];
      const isClosed = !!rt60 && rt60 <= targetRT60;
      bandsClosed[index] = isClosed;
      if (isClosed) count++;
    });
    return { count, bandsClosed };
  }

  function createEmptySolution() {
    return {
      wallQuantity: 0,
      ceilingQuantity: 0,
      wallSqFt: 0,
      ceilingSqFt: 0,
      wallNeeded: 0,
      ceilingNeeded: 0,
      rt60ByFrequency: {},
      bandsClosedCount: 0,
      bandsClosed: [false, false, false, false, false],
      meetsTarget: false,
      showContactSales: false,
    };
  }

  function finalizeSolution(solution, targetRT60, ctx) {
    BANDS.forEach((frequency) => {
      const rt60 = calculateRT60WithProducts(
        frequency,
        solution.wallQuantity,
        solution.ceilingQuantity,
        ctx,
      );
      solution.rt60ByFrequency[frequency] = round4(rt60);
    });
    const { count, bandsClosed } = countClosedBands(
      solution.rt60ByFrequency,
      targetRT60,
    );
    solution.bandsClosedCount = count;
    solution.bandsClosed = bandsClosed;
    solution.meetsTarget = count >= 4;
    return solution;
  }

  function calculateSingleSurfaceSolution(surface, targetRT60, ctx) {
    const solution = createEmptySolution();
    if (surface !== "wall" && surface !== "ceiling") return solution;

    const product = surface === "wall" ? ctx.wallProduct : ctx.ceilingProduct;
    if (!product) return solution;
    const footprint = Number(product.footprint);
    if (!Number.isFinite(footprint) || footprint < 0) return solution;
    const numericTarget = Number(targetRT60);
    const normalizedTarget =
      Number.isFinite(numericTarget) && numericTarget >= MIN_SINGLE_PRODUCT_RT60
        ? numericTarget
        : MIN_SINGLE_PRODUCT_RT60;

    let quantity = 0;
    BANDS.forEach((frequency) => {
      const bandQuantity = findQuantityToCloseBand(
        frequency,
        surface,
        normalizedTarget,
        ctx,
      );
      if (Number.isFinite(bandQuantity)) {
        quantity = Math.max(quantity, Math.max(0, Math.ceil(bandQuantity)));
      }
    });

    const sqFt = quantity * footprint;
    if (surface === "wall") {
      solution.wallQuantity = quantity;
      solution.wallSqFt = sqFt;
      solution.wallNeeded = sqFt;
      solution.exceedsAvailableSpace = sqFt > ctx.availableWallSqFt;
    } else {
      solution.ceilingQuantity = quantity;
      solution.ceilingSqFt = sqFt;
      solution.ceilingNeeded = sqFt;
      solution.exceedsAvailableSpace = sqFt > ctx.availableCeilingSqFt;
    }

    return finalizeSolution(solution, normalizedTarget, ctx);
  }

  function calculateWallsFirst(targetRT60, ctx) {
    const solution = createEmptySolution();
    if (!ctx.wallProduct || !ctx.ceilingProduct) return solution;

    let maxWallQuantity = 0;
    BANDS.forEach((frequency) => {
      const q = findQuantityToCloseBand(frequency, "wall", targetRT60, ctx);
      if (frequency !== "250 Hz" && q > maxWallQuantity) maxWallQuantity = q;
    });

    solution.wallNeeded = maxWallQuantity * ctx.wallProduct.footprint;
    const wouldNeedSqFt = maxWallQuantity * ctx.wallProduct.footprint;
    solution.wallQuantity =
      wouldNeedSqFt > ctx.availableWallSqFt
        ? Math.floor(ctx.availableWallSqFt / ctx.wallProduct.footprint)
        : maxWallQuantity;
    solution.wallSqFt = solution.wallQuantity * ctx.wallProduct.footprint;

    const rt60WithWalls = {};
    BANDS.forEach((frequency) => {
      rt60WithWalls[frequency] = round4(
        calculateRT60WithProducts(frequency, solution.wallQuantity, 0, ctx),
      );
    });

    let maxCeilingQuantity = 0;
    BANDS.forEach((frequency) => {
      if (rt60WithWalls[frequency] > targetRT60) {
        const existingWithWalls =
          calculateExistingAbsorption(frequency, ctx) +
          solution.wallQuantity * ctx.wallProduct.absorption[frequency];
        const stillNeeded =
          calculateRequiredAbsorption(ctx.roomVolume, targetRT60) -
          existingWithWalls;
        const needed = calculateProductQuantity(
          stillNeeded,
          ctx.ceilingProduct.absorption[frequency],
        );
        if (frequency !== "250 Hz" && needed > maxCeilingQuantity) {
          maxCeilingQuantity = needed;
        }
      }
    });

    solution.ceilingNeeded = maxCeilingQuantity * ctx.ceilingProduct.footprint;
    solution.ceilingQuantity = maxCeilingQuantity;
    solution.ceilingSqFt =
      solution.ceilingQuantity * ctx.ceilingProduct.footprint;

    return finalizeSolution(solution, targetRT60, ctx);
  }

  function calculateCeilingFirst(targetRT60, ctx) {
    const solution = createEmptySolution();
    if (!ctx.wallProduct || !ctx.ceilingProduct) return solution;

    let maxCeilingQuantity = 0;
    BANDS.forEach((frequency) => {
      const q = findQuantityToCloseBand(frequency, "ceiling", targetRT60, ctx);
      if (frequency !== "250 Hz" && q > maxCeilingQuantity)
        maxCeilingQuantity = q;
    });

    if (ctx.availableCeilingSqFt === 0) return solution;

    solution.ceilingNeeded = maxCeilingQuantity * ctx.ceilingProduct.footprint;
    const maxCeilingUnits = Math.floor(
      ctx.availableCeilingSqFt / ctx.ceilingProduct.footprint,
    );
    solution.ceilingQuantity = Math.min(maxCeilingQuantity, maxCeilingUnits);
    solution.ceilingSqFt =
      solution.ceilingQuantity * ctx.ceilingProduct.footprint;

    let maxWallQuantity = 0;
    BANDS.forEach((frequency) => {
      const existingAbs = calculateExistingAbsorption(frequency, ctx);
      const ceilingAbs =
        solution.ceilingQuantity * ctx.ceilingProduct.absorption[frequency];
      const totalAbsWithCeiling = existingAbs + ceilingAbs;
      const rt60WithCeiling = calculateRT60(
        ctx.roomVolume,
        totalAbsWithCeiling,
      );

      if (rt60WithCeiling > targetRT60) {
        const wallProductAbs = ctx.wallProduct.absorption[frequency];
        let wallNeeded = 0;
        if (wallProductAbs > 0) {
          const requiredUnits =
            (RT60_CONSTANT * ctx.roomVolume) / (targetRT60 * wallProductAbs);
          const existingUnits = totalAbsWithCeiling / wallProductAbs;
          wallNeeded = Math.ceil(Math.max(requiredUnits - existingUnits, 0));
        }
        if (frequency !== "250 Hz" && wallNeeded > maxWallQuantity) {
          maxWallQuantity = wallNeeded;
        }
      }
    });

    solution.wallNeeded = maxWallQuantity * ctx.wallProduct.footprint;
    solution.wallQuantity = maxWallQuantity;
    solution.wallSqFt = solution.wallQuantity * ctx.wallProduct.footprint;

    return finalizeSolution(solution, targetRT60, ctx);
  }

  function calculateBalanced(targetRT60, wallsFirst, ceilingFirst, ctx) {
    const solution = createEmptySolution();
    if (!ctx.wallProduct || !ctx.ceilingProduct) return solution;

    const option1Wall = wallsFirst ? wallsFirst.wallQuantity : 0;
    const option3Wall = ceilingFirst ? ceilingFirst.wallQuantity : 0;

    const initialWallQuantity = Math.ceil((option1Wall + option3Wall) / 2);
    solution.wallQuantity = initialWallQuantity;
    solution.wallSqFt = solution.wallQuantity * ctx.wallProduct.footprint;
    solution.wallNeeded = initialWallQuantity * ctx.wallProduct.footprint;

    const rt60WithWalls = {};
    BANDS.forEach((frequency) => {
      rt60WithWalls[frequency] = round4(
        calculateRT60WithProducts(frequency, solution.wallQuantity, 0, ctx),
      );
    });

    let maxCeilingQuantity = 0;
    BANDS.forEach((frequency) => {
      if (frequency === "250 Hz") return;
      if (rt60WithWalls[frequency] > targetRT60) {
        const existingWithWalls =
          calculateExistingAbsorption(frequency, ctx) +
          solution.wallQuantity * ctx.wallProduct.absorption[frequency];
        const stillNeeded =
          calculateRequiredAbsorption(ctx.roomVolume, targetRT60) -
          existingWithWalls;
        const needed = calculateProductQuantity(
          stillNeeded,
          ctx.ceilingProduct.absorption[frequency],
        );
        if (needed > maxCeilingQuantity) maxCeilingQuantity = needed;
      }
    });

    solution.ceilingQuantity = maxCeilingQuantity;
    solution.ceilingSqFt =
      solution.ceilingQuantity * ctx.ceilingProduct.footprint;
    solution.ceilingNeeded = maxCeilingQuantity * ctx.ceilingProduct.footprint;

    return finalizeSolution(solution, targetRT60, ctx);
  }

  ns.calculateProductQuantity = calculateProductQuantity;
  ns.findQuantityToCloseBand = findQuantityToCloseBand;
  ns.countClosedBands = countClosedBands;
  ns.createEmptySolution = createEmptySolution;
  ns.finalizeSolution = finalizeSolution;
  ns.calculateSingleSurfaceSolution = calculateSingleSurfaceSolution;
  ns.calculateWallsFirst = calculateWallsFirst;
  ns.calculateCeilingFirst = calculateCeilingFirst;
  ns.calculateBalanced = calculateBalanced;
})();
